
/*
 CIGAR VARIANT TABLE — JAVASCRIPT
 Theme: Stiletto 6.2.0
 Security and quantity validation revision
*/

(() => {
  'use strict';

  const initialized = new WeakSet();
  const roots = new Set();
  const remainingLimits = new WeakMap();

  let cartBusy = false;
  let cartReadVersion = 0;

  // =========================================
  // QUANTITY RULES
  // =========================================

  function readInteger(
    row, attribute, fallback, allowZero = false
  ) {
    if (!row.hasAttribute(attribute)) return fallback;

    const raw = row.getAttribute(attribute);
    const value = Number(raw);

    return raw !== '' &&
      Number.isSafeInteger(value) &&
      (allowZero ? value >= 0 : value > 0)
      ? value
      : null;
  }

  
function getRules(row) {
  const min = readInteger(row, 'data-min', 1);
  const step = readInteger(row, 'data-step', 1);

  const maxRaw = row.getAttribute('data-max');

  const max =
    maxRaw === null || maxRaw.trim() === ''
      ? Infinity
      : readInteger(row, 'data-max', Infinity, true);

  const valid =
    min !== null &&
    step !== null &&
    max !== null &&
    max >= min;

  return {
    min: min ?? 1,
    step: step ?? 1,
    max: valid ? max : 0,
    valid
  };
}


  
  function getMaximum(rules, remaining) {
    if (!rules.valid) return 0;

    const cap = Math.min(rules.max, remaining);

    if (cap === Infinity) return Infinity;

    if (cap < rules.min) return 0;

    return rules.min +
      Math.floor(
        (cap - rules.min) / rules.step
      ) * rules.step;
  }


  function isValidQuantity(raw, rules, maximum) {
    if (!/^[0-9]+$/.test(String(raw).trim())) {
      return false;
    }

    const n = Number(raw);

    return Number.isSafeInteger(n) &&
      n >= rules.min &&
      n <= maximum &&
      (n - rules.min) % rules.step === 0;
  }

  function limitsFor(row) {
    const rules = getRules(row);

    const remaining = remainingLimits.has(row)
      ? remainingLimits.get(row)
      : rules.max;

    return {
      rules,
      maximum: getMaximum(rules, remaining)
    };
  }

  // =========================================
  // CUSTOMER MESSAGES
  // =========================================

  function showMessage(row, message, error = false) {
    const element = row.querySelector(
      '[data-cvt-message]'
    );

    if (!element) return;

    element.textContent = message;
    element.dataset.error = String(error);
  }

  // =========================================
  // UPDATE QUANTITY CONTROLS
  // =========================================

  function updateRow(row, announce = false) {
    const input = row.querySelector('.cvt__input');

    if (!input) return false;

    const { rules, maximum } = limitsFor(row);

    const available =
      rules.valid && maximum >= rules.min;

    const valid =
      available &&
      isValidQuantity(input.value, rules, maximum);

    const n = Number(input.value);

    // Never automatically change typed quantities.
    
      input.min = String(rules.min);
      if (Number.isFinite(maximum)) {
        input.max = String(
          available ? maximum : rules.min
        );
      } else {
        input.removeAttribute('max');
      }
      input.step = String(rules.step);


    
    const message = !rules.valid
      ? 'Invalid product quantity configuration.'
      : !available
        ? 'No additional quantity is available for this item.'
        : !valid
          ? Number.isFinite(maximum)
            ? `Enter ${rules.min} to ${maximum} in increments of ${rules.step}.`
            : `Enter at least ${rules.min} in increments of ${rules.step}.`
          : '';


    input.setCustomValidity(message);

    input.setAttribute(
      'aria-invalid',
      String(!valid)
    );

    const minus = row.querySelector(
      '[data-cvt-step="-1"]'
    );

    const plus = row.querySelector(
      '[data-cvt-step="1"]'
    );

    if (minus) {
      minus.disabled =
        cartBusy ||
        !available ||
        !valid ||
        n <= rules.min;
    }

    if (plus) {
      plus.disabled =
        cartBusy ||
        !available ||
        !valid ||
        n + rules.step > maximum;
    }

    const hidden = row.querySelector(
      '[data-cvt-form-quantity]'
    );

    if (hidden) {
      hidden.value = valid ? String(n) : '';
    }

    const add = row.querySelector('.cvt__add');

    if (add) {
      add.disabled = cartBusy || !valid;
    }

    if (announce) {
      showMessage(row, message, Boolean(message));
    }

    return valid;
  }

  function updateAllRows() {
    for (const root of roots) {
      if (!root.isConnected) {
        roots.delete(root);
        continue;
      }

      root.querySelectorAll(
        '[data-cvt-row]'
      ).forEach(row => updateRow(row));
    }
  }

  // =========================================
  // SHOPIFY CART URL
  // =========================================

  function cartPath(root, suffix) {
    const base = root.dataset.rootUrl || '/';

    const path =
      base.startsWith('/') &&
      !base.startsWith('//') &&
      !base.includes('\\') &&
      !base.includes('?') &&
      !base.includes('#')
        ? base
        : '/';

    return (
      path.endsWith('/') ? path : path + '/'
    ) + suffix;
  }

  // =========================================
  // RETRIEVE SHOPIFY CART
  // =========================================

  
async function readCart(root) {
  const controller = new AbortController();

  const timeoutId = setTimeout(
    () => controller.abort(),
    12000
  );

  try {
    const response = await fetch(
      cartPath(root, 'cart.js'),
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        },
        cache: 'no-store',
        signal: controller.signal
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to retrieve your cart.'
      );
    }

    const cart = await response.json();

    if (!cart || !Array.isArray(cart.items)) {
      throw new Error(
        'Unexpected cart response.'
      );
    }

    return cart;
  } finally {
    clearTimeout(timeoutId);
  }
}


  // =========================================
  // APPLY EXISTING CART QUANTITIES
  // =========================================

  function applyCartLimits(cart) {
    const totals = new Map();

    for (const item of cart.items) {
      const id = String(item.variant_id);
      const qty = Number(item.quantity);

      if (
        Number.isSafeInteger(qty) &&
        qty >= 0
      ) {
        totals.set(
          id,
          (totals.get(id) || 0) + qty
        );
      }
    }

    for (const root of roots) {
      if (!root.isConnected) {
        roots.delete(root);
        continue;
      }

      root.querySelectorAll(
        '[data-cvt-row]'
      ).forEach(row => {
        const rules = getRules(row);

        const already =
          totals.get(
            String(row.dataset.variantId)
          ) || 0;

        
    remainingLimits.set(
      row,
      Number.isFinite(rules.max)
        ? Math.max(0, rules.max - already)
        : Infinity
    );


        updateRow(row);
      });
    }
  }

  async function refreshCartLimits(root) {
    const version = ++cartReadVersion;

    const cart = await readCart(root);

    if (version === cartReadVersion) {
      applyCartLimits(cart);
    }

    return cart;
  }

  // =========================================
  // SAFE SHOPIFY ERROR MESSAGES
  // =========================================

  function errorFromResponse(result, fallback) {
    if (
      result &&
      typeof result.description === 'string'
    ) {
      return result.description;
    }

    if (
      result &&
      typeof result.message === 'string'
    ) {
      return result.message;
    }

    return fallback;
  }

  // =========================================
  // ADD TO CART
  // =========================================

  async function addToCart(root, row, form) {
    if (cartBusy) return;

    const button = form.querySelector('.cvt__add');
    const input = row.querySelector('.cvt__input');

    if (!button || !input) return;

    const variantId = Number(
      row.dataset.variantId
    );

    if (
      !Number.isSafeInteger(variantId) ||
      variantId <= 0
    ) {
      showMessage(
        row,
        'Invalid product variant.',
        true
      );
      return;
    }

    // Preserve the exact customer request.
    const requested = input.value;
    const originalLabel = button.textContent;

    if (!updateRow(row, true)) return;

    cartBusy = true;
    updateAllRows();

    button.textContent = 'Adding...';
    showMessage(row, '');

    let added = false;

    try {
      // Read the latest Shopify cart.
      const cart = await readCart(root);

      applyCartLimits(cart);

      const { rules, maximum } = limitsFor(row);

      if (
        !isValidQuantity(
          requested,
          rules,
          maximum
        )
      ) {
        
      throw new Error(
        maximum < rules.min
          ? 'No additional quantity is available for this item.'
          : Number.isFinite(maximum)
            ? `Quantity unavailable. Enter ${rules.min} to ${maximum} in increments of ${rules.step}.`
            : `Quantity unavailable. Minimum ${rules.min}, increments of ${rules.step}.`
      );

      }

      const quantity = Number(requested);

      const hidden = form.querySelector(
        '[data-cvt-form-quantity]'
      );

      if (hidden) {
        hidden.value = String(quantity);
      }

      // Submit to Shopify's AJAX Cart API.
      const response = await fetch(
        cartPath(root, 'cart/add.js'),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: quantity
              }
            ]
          })
        }
      );

      const result = await response.json()
        .catch(() => null);

      if (!response.ok) {
        throw new Error(
          errorFromResponse(
            result,
            'Unable to add this item.'
          )
        );
      }

      if (!result) {
        throw new Error(
          'Cart confirmation missing. Check your cart before retrying.'
        );
      }

      added = true;

      showMessage(
        row,
        `Added ${quantity} to cart.`
      );

      // Existing Stiletto notification hook.
      document.dispatchEvent(
        new CustomEvent(
          'apps:product-added-to-cart'
        )
      );

      // Reset only after a confirmed addition.
      input.value = String(rules.min);

    } catch (error) {
      showMessage(
        row,
        error instanceof Error
          ? error.message
          : 'Unable to add this item.',
        true
      );

    } finally {
      try {
        await refreshCartLimits(root);
      } catch (_) {
        for (const activeRoot of roots) {
          activeRoot.querySelectorAll(
            '[data-cvt-row]'
          ).forEach(activeRow => {
            remainingLimits.delete(activeRow);
          });
        }

        if (added) {
          showMessage(
            row,
            'Added to cart. Refresh the page to check updated quantity limits.'
          );
        }
      }

      cartBusy = false;
      button.textContent = originalLabel;

      updateAllRows();
    }
  }

  // =========================================
  // INITIALIZE TABLE
  // =========================================

  function initialize(root) {
    if (initialized.has(root)) return;

    initialized.add(root);
    roots.add(root);

    root.querySelectorAll(
      '[data-cvt-row]'
    ).forEach(row => updateRow(row));

    // =======================================
    // PLUS / MINUS BUTTONS
    // =======================================

    root.addEventListener('click', event => {
      const button = event.target.closest?.(
        '[data-cvt-step]'
      );

      if (
        !button ||
        !root.contains(button) ||
        cartBusy ||
        button.disabled
      ) {
        return;
      }

      event.preventDefault();

      const row = button.closest(
        '[data-cvt-row]'
      );

      if (!row) return;

      const input = row.querySelector(
        '.cvt__input'
      );

      if (!input) return;

      const { rules, maximum } = limitsFor(row);

      if (
        !isValidQuantity(
          input.value,
          rules,
          maximum
        )
      ) {
        updateRow(row, true);
        return;
      }

      const direction = Number(
        button.dataset.cvtStep
      );

      if (
        direction !== 1 &&
        direction !== -1
      ) {
        return;
      }

      const next =
        Number(input.value) +
        direction * rules.step;

      if (
        !isValidQuantity(
          String(next),
          rules,
          maximum
        )
      ) {
        return;
      }

      input.value = String(next);

      updateRow(row, true);
    });

    // =======================================
    // MANUAL QUANTITY VALIDATION
    // =======================================

    for (const type of [
      'input',
      'change',
      'focusout'
    ]) {
      root.addEventListener(type, event => {
        if (
          !event.target.matches?.(
            '.cvt__input'
          )
        ) {
          return;
        }

        const row = event.target.closest(
          '[data-cvt-row]'
        );

        if (row) {
          updateRow(
            row,
            type !== 'input'
          );
        }
      });
    }

    // =======================================
    // AJAX FORM SUBMISSION
    // =======================================

    root.addEventListener(
      'submit',
      event => {
        const form = event.target;

        if (
          !form.matches?.(
            '[data-cvt-form]'
          )
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        const row = form.closest(
          '[data-cvt-row]'
        );

        if (row && !cartBusy) {
          addToCart(root, row, form);
        }
      },
      true
    );

    // Load existing cart quantities.
    refreshCartLimits(root).catch(() => {
      // Retain Liquid-provided quantity limits.
    });
  }

  // =========================================
  // INITIAL PAGE LOAD
  // =========================================

  function mount(scope = document) {
    if (
      scope.matches?.(
        '[data-cvt-root]'
      )
    ) {
      initialize(scope);
    }

    scope.querySelectorAll?.(
      '[data-cvt-root]'
    ).forEach(initialize);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => mount(),
      { once: true }
    );
  } else {
    mount();
  }


  // =========================================
  // REFRESH CART WHEN CUSTOMER RETURNS
  // =========================================

  let refreshInProgress = false;

  async function refreshVisibleTables() {
    if (
      cartBusy ||
      refreshInProgress ||
      document.hidden
    ) {
      return;
    }

    const root = [...roots].find(
      item => item.isConnected
    );

    if (!root) return;

    refreshInProgress = true;

    try {
      await refreshCartLimits(root);
    } catch (_) {
      // Discard stale cart quantity limits.
      for (const activeRoot of roots) {
        if (!activeRoot.isConnected) continue;

        activeRoot.querySelectorAll(
          '[data-cvt-row]'
        ).forEach(row => {
          remainingLimits.delete(row);
          updateRow(row);
        });
      }
    } finally {
      refreshInProgress = false;
    }
  }

  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) {
        refreshVisibleTables();
      }
    }
  );

  window.addEventListener(
    'pageshow',
    event => {
      if (event.persisted) {
        refreshVisibleTables();
      }
    }
  );


  // =========================================
  // SHOPIFY THEME EDITOR SUPPORT
  // =========================================

  document.addEventListener(
    'shopify:section:load',
    event => {
      mount(event.target);
    }
  );

})();
